import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { getToken } from "@/lib/session";
import SignaturePad from "@/components/SignaturePad";
import {
  ShieldCheck,
  CheckCircle2,
  Zap,
  Sun,
  Wrench,
  ClipboardCheck,
  Calendar,
  Phone,
  Mail,
  MapPin,
  FileSignature,
  Award,
} from "lucide-react";

/**
 * Shared, industry-standard PowerBuilt Handoff Report template.
 * Rendered on client + admin pages. Handles print-mode automatically.
 *
 * Props:
 *  - bundle: /handoff response payload
 *  - mode: "client" | "admin"
 *  - onSigned(): callback after client signs (client mode only)
 */
export default function HandoffReport({ bundle: b, mode = "client", onSigned }) {
  const materialsByCategory = useMemo(() => {
    const map = {};
    ((b && b.materials_reserved) || []).forEach((m) => {
      const cat = (m.category || "general").toLowerCase();
      (map[cat] = map[cat] || []).push(m);
    });
    return map;
  }, [b]);

  if (!b) return null;
  const p = b.project || {};
  const c = b.client || {};
  const sig = b.handoff_signature || p.handoff_signature;
  const cert = b.handoff_cert_id || p.handoff_cert_id;
  const commissioning = b.commissioning || p.commissioning;
  const isSigned = !!(sig && sig.data_url);

  return (
    <div data-testid="handoff-report" className="max-w-4xl mx-auto space-y-6 pb-16 print:pb-0">
      {/* Cover header — PowerBuilt seal */}
      <div className="relative overflow-hidden rounded-2xl border-2 border-[#C9A356]/30 bg-gradient-to-br from-[#0A2540] via-[#12365b] to-[#0A2540] text-white p-8 sm:p-10 print:border-0 print:rounded-none">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #C9A356 0%, transparent 40%), radial-gradient(circle at 90% 90%, #ffffff 0%, transparent 40%)" }} />
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#C9A356] font-bold">
              <Award className="w-3.5 h-3.5" /> Certificate of Handoff
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl mt-3 leading-tight">
              {p.title || "PowerBuilt System"}
            </h1>
            <div className="text-xs text-white/70 mt-2 tracking-wide">
              {(p.project_type || "").replace(/_/g, " ").toUpperCase()}
              {p.kw_target ? ` · ${p.kw_target} kW` : ""}
              {p.project_number ? ` · ${p.project_number}` : ""}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Issued by</div>
            <div className="font-heading text-lg mt-1">POWERBUILT SXM</div>
            <div className="text-[10px] text-white/60 mt-1">Solar · Electrical · Maintenance</div>
            {cert && (
              <div className="mt-3 inline-block px-3 py-1 rounded-full border border-[#C9A356]/40 text-[10px] text-[#C9A356] font-mono">
                {cert}
              </div>
            )}
          </div>
        </div>

        {isSigned && (
          <div className="absolute top-8 right-8 -rotate-12 pointer-events-none hidden sm:block">
            <div className="border-4 border-emerald-400 text-emerald-400 rounded-lg px-4 py-2 font-heading tracking-widest text-xl opacity-90">
              ACCEPTED
            </div>
          </div>
        )}
      </div>

      {/* Parties — client + site + dates */}
      <div className="grid md:grid-cols-3 gap-4">
        <PartyCard title="Client" icon={FileSignature}>
          <div className="text-sm font-semibold text-[#0A2540] dark:text-white">{c.name || "—"}</div>
          <div className="text-xs text-[#475569] dark:text-slate-400 font-mono">
            {c.customer_number || c.customer_code || ""}
          </div>
          {c.email && (
            <div className="text-xs text-[#475569] dark:text-slate-400 inline-flex items-center gap-1 mt-2">
              <Mail className="w-3 h-3" /> {c.email}
            </div>
          )}
          {c.phone && (
            <div className="text-xs text-[#475569] dark:text-slate-400 inline-flex items-center gap-1">
              <Phone className="w-3 h-3" /> {c.phone}
            </div>
          )}
        </PartyCard>

        <PartyCard title="Installation Site" icon={MapPin}>
          <div className="text-sm text-[#0A2540] dark:text-white">
            {p.site_address || "Site details on file"}
          </div>
          {p.property_id && (
            <div className="text-[10px] text-[#94A3B8] font-mono mt-1">{p.property_id}</div>
          )}
        </PartyCard>

        <PartyCard title="Key Dates" icon={Calendar}>
          <DateLine label="Ordered" value={p.created_at} />
          <DateLine label="Installed" value={p.install_date} />
          <DateLine label="Report generated" value={b.generated_at} />
          {isSigned && <DateLine label="Signed" value={sig.signed_at} highlight />}
        </PartyCard>
      </div>

      {/* Scope of work */}
      <Section title="Scope of work delivered" icon={ClipboardCheck}>
        <p className="text-sm text-[#0A2540] dark:text-white leading-relaxed whitespace-pre-wrap">
          {p.scope ||
            `Design, supply, install and commission a ${p.kw_target ? `${p.kw_target} kW ` : ""}${
              (p.project_type || "").replace(/_/g, " ")
            } system at the address of record. Includes labour, materials, testing, safety compliance and handover documentation.`}
        </p>
      </Section>

      {/* Equipment installed — grouped by category */}
      <Section title="Equipment installed" icon={Sun}>
        {Object.keys(materialsByCategory).length === 0 ? (
          <p className="text-xs text-[#94A3B8] italic">No equipment recorded.</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(materialsByCategory).map(([cat, items]) => (
              <div key={cat}>
                <div className="text-[10px] uppercase tracking-wider text-[#C9A356] font-bold mb-1">
                  {cat}
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-[#E2E8F0] dark:divide-slate-700">
                    {items.map((m) => (
                      <tr key={m.reservation_id}>
                        <td className="py-1.5 text-[#0A2540] dark:text-white">{m.material_name}</td>
                        <td className="py-1.5 text-right tabular-nums text-[#475569] dark:text-slate-400 whitespace-nowrap">
                          {m.qty} {m.unit || "ea"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Commissioning tests */}
      <Section title="Commissioning & test results" icon={Zap}>
        {!commissioning || Object.keys(commissioning).length === 0 ? (
          <p className="text-xs text-[#94A3B8] italic">
            Commissioning values will be recorded by the install team before handoff.
          </p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <MetricRow label="PV open-circuit voltage" value={commissioning.pv_open_circuit_v} unit="V" />
              <MetricRow label="String voltage" value={commissioning.string_voltage} unit="V" />
              <MetricRow label="Insulation resistance" value={commissioning.insulation_test} unit="MΩ" />
              <MetricRow label="Earth bond" value={commissioning.earth_bond_ohms} unit="Ω" />
              <MetricRow label="Inverter AC output" value={commissioning.inverter_output_ac} unit="V" />
              <MetricRow label="Battery state of charge" value={commissioning.battery_soc} unit="%" />
              <MetricRow label="Grid export test" value={commissioning.grid_export_test} />
              <MetricRow
                label="Monitoring portal"
                value={
                  commissioning.monitoring_verified === true
                    ? "Verified & live"
                    : commissioning.monitoring_verified === false
                    ? "Not verified"
                    : ""
                }
              />
            </div>
            {commissioning.notes && (
              <p className="mt-3 text-xs text-[#475569] dark:text-slate-400 whitespace-pre-wrap border-t pt-3 border-[#E2E8F0] dark:border-slate-700">
                {commissioning.notes}
              </p>
            )}
            <div className="mt-3 text-[10px] uppercase tracking-wider text-[#94A3B8]">
              Tested by <span className="text-[#0A2540] dark:text-white font-semibold">{commissioning.tested_by || "PowerBuilt Team"}</span>
              {commissioning.tested_at && ` · ${new Date(commissioning.tested_at).toLocaleDateString()}`}
            </div>
          </>
        )}
      </Section>

      {/* Warranty */}
      {b.warranty && (
        <Section title="Warranty" icon={ShieldCheck}>
          <div className="flex items-baseline gap-3">
            <div className="font-heading text-3xl text-[#0A2540] dark:text-white">{b.warranty.years}</div>
            <div className="text-sm text-[#475569] dark:text-slate-400">year workmanship warranty</div>
          </div>
          <div className="text-xs text-[#475569] dark:text-slate-400 mt-2">
            Effective <b>{new Date(b.warranty.starts_at).toLocaleDateString()}</b> — expires{" "}
            <b>{new Date(b.warranty.expires_at).toLocaleDateString()}</b>. Manufacturer warranties on
            panels, inverter and battery pass through directly per the equipment datasheets.
          </div>
        </Section>
      )}

      {/* O&M guidance */}
      <Section title="Operation & maintenance" icon={Wrench}>
        <ul className="text-sm text-[#0A2540] dark:text-white space-y-1.5 list-disc pl-5">
          <li>Monitor daily production via the PowerBuilt monitoring portal.</li>
          <li>Keep panels free of debris and heavy soiling; rinse with clean water only.</li>
          <li>Do <b>not</b> operate isolators during a fault — contact PowerBuilt first.</li>
          <li>Schedule an annual health-check with PowerBuilt to preserve warranty.</li>
          <li>In case of grid outage, refer to the battery-backup label at the inverter.</li>
        </ul>
      </Section>

      {/* Timeline */}
      <Section title="Project timeline" icon={Calendar}>
        <ul className="text-sm space-y-1">
          {(b.timeline || []).map((t, i) => (
            <li key={i} className="flex items-start gap-2 border-b border-[#E2E8F0] dark:border-slate-700 py-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <span className="text-[#0A2540] dark:text-white capitalize">
                  {(t.status || "").replace(/_/g, " ")}
                </span>
                {t.note && <span className="text-[#475569] dark:text-slate-400"> — {t.note}</span>}
              </div>
              <span className="text-[10px] text-[#94A3B8] whitespace-nowrap">
                {new Date(t.timestamp).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Punchlist */}
      {(b.punchlist || []).length > 0 && (
        <Section title="Remediation punchlist">
          <ul className="text-sm space-y-2">
            {b.punchlist.map((pl) => (
              <li key={pl.punch_id} className="border-b border-[#E2E8F0] dark:border-slate-700 py-1.5">
                <div className="flex items-start justify-between gap-2">
                  <span className={pl.status === "closed" ? "line-through text-[#94A3B8]" : "text-[#0A2540] dark:text-white"}>
                    {pl.description}
                  </span>
                  <span className="text-[10px] uppercase font-bold text-[#94A3B8] whitespace-nowrap">
                    {pl.status}
                  </span>
                </div>
                {(pl.photos || []).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {pl.photos.map((ph) => (
                      <img
                        key={ph.photo_id}
                        src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}?auth=${encodeURIComponent(getToken() || "")}`}
                        alt={ph.caption || "remediation"}
                        className="w-16 h-16 object-cover rounded border border-[#E2E8F0] dark:border-slate-700"
                      />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Acceptance signature */}
      <AcceptanceBlock
        mode={mode}
        bundle={b}
        onSigned={onSigned}
      />

      <div className="text-center text-[10px] text-[#94A3B8] py-6 print:pt-2 leading-relaxed">
        This certificate is generated by the PowerBuilt operating system on {new Date(b.generated_at).toLocaleString()}.<br/>
        For questions contact your PowerBuilt account manager · admin@powerbuilt.sx
      </div>
    </div>
  );
}

/* ---------------- Sub-components ---------------- */

function Section({ title, icon: Icon, children }) {
  return (
    <div
      className="card-pb p-6 print:shadow-none print:border print:break-inside-avoid"
      data-testid={`ho-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3 inline-flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#C9A356]" />} {title}
      </h2>
      {children}
    </div>
  );
}

function PartyCard({ title, icon: Icon, children }) {
  return (
    <div className="card-pb p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#C9A356] font-bold inline-flex items-center gap-1 mb-2">
        {Icon && <Icon className="w-3 h-3" />} {title}
      </div>
      {children}
    </div>
  );
}

function DateLine({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[#94A3B8]">{label}</span>
      <span className={highlight ? "text-emerald-600 font-semibold" : "text-[#0A2540] dark:text-white"}>
        {value ? new Date(value).toLocaleDateString() : "—"}
      </span>
    </div>
  );
}

function MetricRow({ label, value, unit }) {
  const filled = value !== undefined && value !== null && String(value).trim() !== "";
  return (
    <div className="flex items-center justify-between border-b border-dotted border-[#E2E8F0] dark:border-slate-700 py-1">
      <span className="text-xs text-[#475569] dark:text-slate-400">{label}</span>
      <span className={`text-sm tabular-nums font-semibold ${filled ? "text-[#0A2540] dark:text-white" : "text-[#94A3B8] italic"}`}>
        {filled ? `${value}${unit ? ` ${unit}` : ""}` : "—"}
      </span>
    </div>
  );
}

function AcceptanceBlock({ mode, bundle: b, onSigned }) {
  const p = b.project || {};
  const sig = b.handoff_signature || p.handoff_signature;
  const cert = b.handoff_cert_id || p.handoff_cert_id;
  const isSigned = !!(sig && sig.data_url);

  const [ink, setInk] = useState(null);
  const [name, setName] = useState((b.client && b.client.name) || "");
  const [saving, setSaving] = useState(false);

  if (isSigned) {
    return (
      <Section title="Client acceptance" icon={CheckCircle2}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">
              Signed & accepted
            </div>
            <div className="text-lg font-heading text-[#0A2540] dark:text-white mt-1">
              {sig.signer_name}
            </div>
            <div className="text-xs text-[#475569] dark:text-slate-400">
              {new Date(sig.signed_at).toLocaleString()}
            </div>
            {cert && (
              <div className="text-[10px] font-mono text-[#94A3B8] mt-2">Cert: {cert}</div>
            )}
          </div>
          <img
            src={sig.data_url}
            alt="Client signature"
            className="max-h-24 max-w-[240px] object-contain rounded border border-[#E2E8F0] dark:border-slate-700 bg-white"
          />
        </div>
      </Section>
    );
  }

  if (mode !== "client") {
    return (
      <Section title="Client acceptance" icon={FileSignature}>
        <p className="text-sm text-[#475569] dark:text-slate-400">
          Awaiting client signature. The client can sign this report from their <b>Handoff Reports</b> page — desktop, tablet or mobile.
        </p>
      </Section>
    );
  }

  const submit = async () => {
    if (!ink) { toast.error("Please draw your signature"); return; }
    if (!name.trim()) { toast.error("Type your full name to accept"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/client/projects/${p.project_id}/handoff/sign`, {
        signature: ink,
        signer_name: name.trim(),
      });
      toast.success("Handoff accepted. Thank you!");
      if (onSigned) onSigned(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    } finally { setSaving(false); }
  };

  return (
    <Section title="Client acceptance & sign-off" icon={FileSignature}>
      <p className="text-sm text-[#475569] dark:text-slate-400 mb-4">
        By signing below you confirm the system has been installed, tested and demonstrated to your satisfaction and the warranty terms above have been explained.
      </p>
      <div className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#C9A356] font-bold mb-1">
            Signature
          </label>
          <SignaturePad onChange={setInk} height={180} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#C9A356] font-bold mb-1">
            Full legal name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Darryl York"
            data-testid="handoff-signer-name"
            className="w-full h-11 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm"
          />
        </div>
        <button
          onClick={submit}
          disabled={saving || !ink || !name.trim()}
          data-testid="handoff-accept-btn"
          className="w-full sm:w-auto h-11 px-6 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Recording acceptance…" : "Accept & sign handoff"}
        </button>
      </div>
    </Section>
  );
}
